import { useAccount, useMsal } from '@azure/msal-react';
import { useEnvironment } from '@publicisgroupe/p-os-components/build/contexts/EnvironmentContext';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { POSLoader } from '../components/POSLoader/POSLoader';
import { NavigationItemDto, UserInfo } from '../types/types';
import { webframeApi } from '../api/webframeApi';
import { setBearerToken } from '@publicisgroupe/p-os-components';
import { useHttp } from '@publicisgroupe/p-os-components';
import { getSortedFiltersData } from 'utils/helpers';
import { BrowserRouter } from 'react-router-dom';
import md5 from 'md5';
import Login from 'components/Login';

const defaultUserInfo: UserContext<UserInfo> = {
  navData: [],
  userInfo: {
    userEmail: '',
    userName: '',
    userType: '',
    claims: [],
  },
};

export interface UserContext<T extends UserInfo> {
  userInfo: T;
  navData: NavigationItemDto[];
}

interface AuthProviderProps {
  children?: React.ReactNode;
}

const JWT_TOKEN_REFRESH_TIMEOUT = 900000;
const AuthContext = React.createContext<UserContext<UserInfo>>(defaultUserInfo);

export const AuthProvider = (props: AuthProviderProps) => {
  const [userContext, setUserContext] = useState<UserContext<UserInfo>>(defaultUserInfo);
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [isFilterReady, setIsFilterReady] = useState<boolean>(false);
  const { inProgress, instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const { isLoaded } = useEnvironment();
  const timeoutInstance = useRef<NodeJS.Timeout>();
  const http = useHttp();
  // md5 - User Hashing
  const getUserHash = useCallback(() => {
    if (!account || !account.username) return '';
    return md5(account.username);
  }, [account]);

  useEffect(() => {
    if (!loggedIn || !isLoaded) return;

    webframeApi(http)
      ?.getNavigationData()
      .then(({ data }) => {
        setUserContext((prev) => {
          return {
            ...prev,
            navData:
              getSortedFiltersData(
                data.items?.filter((x: any) => x.items.length > 0) ?? [],
                'name'
              ) ?? [],
          };
        });

        setIsFilterReady(true);
      })
      .catch((err) => {
        console.error('webframe getNavigationData error', err);
      });
  }, [http, isLoaded, loggedIn]);

  useEffect(() => {
    if (!loggedIn || !isLoaded) return;

    webframeApi(http)
      ?.getUserInfo()
      .then(({ data }) => {
        setUserContext((prev) => {
          return {
            ...prev,
            userInfo: {
              ...prev.userInfo,
              claims: [...prev.userInfo.claims, ...data.claims],
              userType: data.userType,
              userName: account?.name ?? '',
              userEmail: account?.username ?? '',
            },
          };
        });

        setIsReady(true);
      })
      .catch((err) => {
        console.error('webframe getUserInfo error', err);
      });
  }, [account?.name, account?.username, http, isLoaded, loggedIn]);

  const acquireTokenSilent = useCallback(
    (initialLoad?: boolean) => {
      if (!account) {
        setLoggedIn(false);
        setIsReady(true);
        return;
      }

      const setRefreshTokenTimeout = (timeout: number) => {
        if (timeoutInstance && timeoutInstance.current) {
          clearTimeout(timeoutInstance.current);
        }

        timeoutInstance.current = setTimeout(acquireTokenSilent, timeout);
      };

      const acquireToken = () =>
        instance
          .acquireTokenSilent({
            scopes: [],
            account: account,
          })
          .then((result: any) => {
            window.bearerToken = result.idToken;
            window.bearerTokenExpiresOn = result.expiresOn;
            window.bearerTokenExtExpiresOn = result.extExpiresOn;
            window.userHash = getUserHash();
            setLoggedIn(true);
            setRefreshTokenTimeout(JWT_TOKEN_REFRESH_TIMEOUT);

            setBearerToken({
              idToken: result.idToken,
              expiresOn: result.expiresOn,
              extExpiresOn: result.extExpiresOn,
              refresh: acquireToken,
            });
          })
          .catch(() => {
            if (window.bearerTokenExtExpiresOn && new Date() > window.bearerTokenExtExpiresOn) {
              console.log('webframe bearerTokenExtExpiresOn');
            }

            if (
              initialLoad ||
              (window.bearerTokenExtExpiresOn && new Date() > window.bearerTokenExtExpiresOn)
            ) {
              // if silent token acquisition fails, fallback to an interactive method
              // only on initialLoad or when token expired without refresh. This can happen when
              // user had no inet connection for extended time or laptop in sleepmode without closing the browser
              // subsequent calls are due to refresh, when page is idle.
              // when idle its possible that user looses internet connection, so we don't set to
              // loggedIn false, it will redirect to login page and user will lose work progress
              setLoggedIn(false);
              setIsReady(true);
            } else {
              // we keep retrying to get a token, but faster. Not on initialLoad
              setTimeout(() => {
                setRefreshTokenTimeout(3000);
              });
            }
          });
      acquireToken();
    },
    [account, instance, getUserHash]
  );

  useEffect(() => {
    if (inProgress !== 'none') return;

    acquireTokenSilent(true);
  }, [acquireTokenSilent, inProgress]);

  const showLoader = (loggedIn && !isFilterReady) || !isReady;
  return (
    <AuthContext.Provider value={userContext}>
      {showLoader && <POSLoader showLoadingText={false} />}
      {isReady && loggedIn && isFilterReady && props.children}
      {isReady && !loggedIn && (
        <BrowserRouter>
          <Login />
        </BrowserRouter>
      )}
    </AuthContext.Provider>
  );
};

export const useUserContext = () => {
  const context = useContext(AuthContext);
  return {
    ...context,
  };
};
